if (!customElements.get('bna-mobile-image-viewer')) {
  customElements.define('bna-mobile-image-viewer', class extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return;
      this.innerHTML = `<dialog class="bna-mobile-viewer" aria-label="Product image viewer" aria-describedby="BnaMobileViewerHelp">
        <div class="bna-mobile-viewer__header"><span data-viewer-counter aria-live="polite" aria-atomic="true"></span><button type="button" data-viewer-close aria-label="Close image viewer">×</button></div>
        <p id="BnaMobileViewerHelp" class="visually-hidden">Swipe left or right to change images at full view. Pinch or double tap to zoom, then drag to explore. Use the zoom buttons or arrow keys as an alternative. Escape closes the viewer.</p>
        <div class="bna-mobile-viewer__stage" tabindex="0" aria-label="Image viewing area"><img alt="" draggable="false"><button type="button" class="bna-mobile-viewer__previous" data-viewer-previous aria-label="Previous image">‹</button><button type="button" class="bna-mobile-viewer__next" data-viewer-next aria-label="Next image">›</button></div>
        <div class="bna-mobile-viewer__tools" role="group" aria-label="Image zoom"><button type="button" data-viewer-out aria-label="Zoom out">−</button><span data-viewer-percent>100%</span><button type="button" data-viewer-in aria-label="Zoom in">+</button><button type="button" data-viewer-reset>Reset</button></div>
        <span class="visually-hidden" data-viewer-status role="status" aria-live="polite"></span>
      </dialog>`;
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.bna-mobile-viewer__stage');
      this.image = this.stage.querySelector('img');
      this.pointers = new Map();
      this.scale = 1; this.x = this.y = 0;
    }
    open(owner) {
      if (this.dialog.open || !owner.isConnected) return;
      const gallery = owner.closest('media-gallery') || owner.parentElement;
      this.items = [...gallery.querySelectorAll('bna-image-zoom')].filter(item => item.querySelector('.product__media img'));
      this.index = this.items.indexOf(owner);
      if (this.index < 0) return;
      this.owner = owner; this.returnFocus = owner.toggle || owner.querySelector('[data-zoom-toggle]');
      this.lifecycle = new AbortController();
      const on = (node, event, fn, options = {}) => node.addEventListener(event, fn, { ...options, signal: this.lifecycle.signal });
      on(this.querySelector('[data-viewer-close]'), 'click', () => this.close());
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      on(this.dialog, 'close', () => { if (!this.dialog.open) this.close(); });
      on(this.querySelector('[data-viewer-previous]'), 'click', () => this.step(-1));
      on(this.querySelector('[data-viewer-next]'), 'click', () => this.step(1));
      on(this.querySelector('[data-viewer-in]'), 'click', () => this.zoom(this.scale + .5));
      on(this.querySelector('[data-viewer-out]'), 'click', () => this.zoom(this.scale - .5));
      on(this.querySelector('[data-viewer-reset]'), 'click', () => this.zoom(1));
      on(this.dialog, 'keydown', event => this.keydown(event));
      on(this.stage, 'pointerdown', event => this.pointerDown(event));
      on(this.stage, 'pointermove', event => this.pointerMove(event));
      on(this.stage, 'pointerup', event => this.pointerUp(event));
      on(this.stage, 'pointercancel', event => this.pointerUp(event, true));
      on(this.stage, 'lostpointercapture', event => { if (this.pointers.has(event.pointerId)) this.pointerUp(event, true); });
      on(this.stage, 'touchmove', event => { if (!event.target.closest('button') && event.cancelable) event.preventDefault(); }, { passive: false });
      on(this.image, 'load', () => this.paint());
      on(window, 'resize', () => this.paint());
      on(document, 'shopify:section:unload', event => { if (event.target.contains(this.owner)) this.close(); });
      this.galleryObserver = new MutationObserver(() => {
        if (!owner.isConnected || this.items.some(item => !gallery.contains(item))) this.close();
      });
      this.galleryObserver.observe(document.body, { childList: true, subtree: true });
      this.lockScroll();
      this.dialog.showModal();
      this.show(this.index);
      this.resizeObserver = new ResizeObserver(() => this.paint());
      this.resizeObserver.observe(this.stage);
      this.querySelector('[data-viewer-close]').focus({ preventScroll: true });
    }
    lockScroll() {
      this.scroll = { x: window.scrollX, y: window.scrollY, styles: [] };
      const set = (node, key, value) => {
        this.scroll.styles.push([node, key, node.style.getPropertyValue(key), node.style.getPropertyPriority(key)]);
        node.style.setProperty(key, value);
      };
      set(document.documentElement, 'overflow', 'hidden');
      set(document.documentElement, 'overscroll-behavior', 'none');
      set(document.body, 'position', 'fixed');
      set(document.body, 'top', `${-this.scroll.y}px`);
      set(document.body, 'left', `${-this.scroll.x}px`);
      set(document.body, 'width', '100%');
      set(document.body, 'overflow', 'hidden');
    }
    close() {
      if (!this.lifecycle) return;
      this.lifecycle.abort(); this.lifecycle = null;
      this.galleryObserver?.disconnect(); this.resizeObserver?.disconnect();
      this.pointers.clear(); this.gesture = this.tap = null;
      if (this.dialog.open) this.dialog.close();
      const scroll = this.scroll; this.scroll = null;
      if (scroll) {
        for (const [node, key, value, priority] of scroll.styles) {
          if (value) node.style.setProperty(key, value, priority); else node.style.removeProperty(key);
        }
        const root = document.documentElement;
        const behavior = root.style.getPropertyValue('scroll-behavior'), priority = root.style.getPropertyPriority('scroll-behavior');
        root.style.setProperty('scroll-behavior', 'auto', 'important');
        window.scrollTo(scroll.x, scroll.y);
        if (behavior) root.style.setProperty('scroll-behavior', behavior, priority); else root.style.removeProperty('scroll-behavior');
      }
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.owner = this.returnFocus = null;
    }
    disconnectedCallback() { this.close(); }
    show(index) {
      this.index = index;
      const source = this.items[index].querySelector('.product__media img');
      this.image.alt = source.alt || '';
      this.image.width = source.naturalWidth || source.width;
      this.image.height = source.naturalHeight || source.height;
      this.image.src = source.currentSrc || source.src;
      if (source.srcset) this.image.srcset = source.srcset; else this.image.removeAttribute('srcset');
      this.image.sizes = `${Math.ceil(this.stage.clientWidth * 3)}px`;
      this.querySelector('[data-viewer-counter]').textContent = `${index + 1} / ${this.items.length}`;
      this.querySelector('[data-viewer-previous]').disabled = index === 0;
      this.querySelector('[data-viewer-next]').disabled = index === this.items.length - 1;
      this.scale = 1; this.x = this.y = 0; this.pointers.clear(); this.gesture = this.tap = null;
      this.paint();
    }
    step(delta) {
      const index = Math.max(0, Math.min(this.items.length - 1, this.index + delta));
      if (index !== this.index) this.show(index);
    }
    bounds() {
      const w = this.stage.clientWidth, h = this.stage.clientHeight;
      const ratio = (this.image.naturalWidth || this.image.width || 1) / (this.image.naturalHeight || this.image.height || 1);
      const width = Math.min(w, h * ratio), height = width / ratio;
      return { width, height, maxX: Math.max(0, (width * this.scale - w) / 2), maxY: Math.max(0, (height * this.scale - h) / 2) };
    }
    zoom(scale, point) {
      const next = Math.max(1, Math.min(4, scale)), ratio = next / this.scale;
      const anchor = point || { x: 0, y: 0 };
      this.x = anchor.x - (anchor.x - this.x) * ratio;
      this.y = anchor.y - (anchor.y - this.y) * ratio;
      this.scale = next;
      if (next === 1) this.x = this.y = 0;
      this.paint(); this.announceZoom();
    }
    paint() {
      if (!this.dialog?.open) return;
      const { width, height, maxX, maxY } = this.bounds();
      this.x = Math.max(-maxX, Math.min(maxX, this.x)); this.y = Math.max(-maxY, Math.min(maxY, this.y));
      Object.assign(this.image.style, { width: `${width}px`, height: `${height}px`, transform: `translate(-50%, -50%) translate(${this.x}px, ${this.y}px) scale(${this.scale})` });
      this.querySelector('[data-viewer-percent]').textContent = `${Math.round(this.scale * 100)}%`;
      this.querySelector('[data-viewer-in]').setAttribute('aria-disabled', String(this.scale === 4));
      this.querySelector('[data-viewer-out]').setAttribute('aria-disabled', String(this.scale === 1));
    }
    announceZoom() { this.querySelector('[data-viewer-status]').textContent = `Zoom ${Math.round(this.scale * 100)} percent`; }
    point(event) {
      const rect = this.stage.getBoundingClientRect();
      return { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
    }
    startGesture(multiple = false) {
      const points = [...this.pointers.values()];
      if (points.length >= 2) {
        const [a, b] = points, center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        this.gesture = { multi: true, distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), scale: this.scale, anchorX: (center.x - this.x) / this.scale, anchorY: (center.y - this.y) / this.scale };
        this.tap = null;
      } else if (points.length === 1) {
        this.gesture = { multi: multiple, start: points[0], x: this.x, y: this.y, scale: this.scale, time: Date.now() };
      }
    }
    pointerDown(event) {
      if (event.target.closest('button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.pointers.set(event.pointerId, this.point(event));
      this.stage.setPointerCapture(event.pointerId);
      this.startGesture(this.gesture?.multi || false);
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      if (event.cancelable) event.preventDefault();
      this.pointers.set(event.pointerId, this.point(event));
      const points = [...this.pointers.values()], g = this.gesture;
      if (points.length >= 2 && g.distance) {
        const [a, b] = points;
        this.scale = Math.max(1, Math.min(4, g.scale * Math.hypot(a.x - b.x, a.y - b.y) / g.distance));
        this.x = (a.x + b.x) / 2 - g.anchorX * this.scale;
        this.y = (a.y + b.y) / 2 - g.anchorY * this.scale;
      } else if (points.length === 1 && g.start && this.scale > 1) {
        this.x = g.x + points[0].x - g.start.x; this.y = g.y + points[0].y - g.start.y;
      }
      this.paint();
    }
    pointerUp(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      const end = this.point(event), g = this.gesture;
      this.pointers.delete(event.pointerId);
      if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
      if (this.pointers.size) { this.startGesture(true); return; }
      this.gesture = null;
      if (cancelled || !g || g.multi || !g.start) { this.tap = null; this.announceZoom(); return; }
      const dx = end.x - g.start.x, dy = end.y - g.start.y, duration = Date.now() - g.time;
      if (g.scale === 1 && this.scale === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2 && duration < 750) {
        this.tap = null; this.step(dx < 0 ? 1 : -1);
      } else if (Math.hypot(dx, dy) < 10 && duration < 300) {
        if (this.tap && Date.now() - this.tap.time < 350 && Math.hypot(end.x - this.tap.x, end.y - this.tap.y) < 32) {
          this.zoom(this.scale > 1 ? 1 : 2.5, end); this.tap = null;
        } else this.tap = { ...end, time: Date.now() };
      } else this.tap = null;
    }
    keydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); return; }
      if (event.key === 'Tab') {
        const nodes = [...this.dialog.querySelectorAll('button:not([disabled]), [tabindex="0"]')];
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        return;
      }
      if (['+', '=', '-', '0'].includes(event.key)) { event.preventDefault(); this.zoom(event.key === '0' ? 1 : this.scale + (event.key === '-' ? -.5 : .5)); return; }
      const delta = { ArrowLeft: [-40, 0], ArrowRight: [40, 0], ArrowUp: [0, -40], ArrowDown: [0, 40] }[event.key];
      if (!delta) return;
      event.preventDefault();
      if (this.scale > 1) { this.x += delta[0]; this.y += delta[1]; this.paint(); }
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') this.step(event.key === 'ArrowLeft' ? -1 : 1);
    }
  });
}
